import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, Link, useHistory } from 'react-router-dom'
import { IconSparkles } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { SearchableDropdown } from '@/components/dropdowns/SearchableDropdown'
import { PageHeader } from '@/components/headers/PageHeader'
import {
  fetchScorecardsByOrg,
  setCurrentOrganizationId,
} from '@/reducers/scorecards/scorecards.actions'
import { loadPlaybooks } from '@/reducers/playbooks/playbooks.actions'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { FilterForm } from '@/components/filters/FilterForm'
import { clearFilters, setData, setFilter } from '@/reducers/scorecards/scorecards.redux'
import { openModal } from '@/reducers/ui/ui.redux'
import { getOrganizationOptions, hasAnyOmnichannelAccess } from '@/utils/helpers'

import QACopilotGuidedSetupModal from '@/views/QACopilot/modals/QACopilotGuidedSetupModal'

import { ScorecardsTable } from './ScorecardsTable'
import { ScorecardWizard } from './forms/AddScorecardForm'

const Scorecards = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { organizations } = useSelector((state) => state)
  const { filters } = useSelector((state) => state.scorecards)
  const currentUser = useSelector((state) => state.currentUser)
  const { currentlyOpenModalId: modal } = useSelector((state) => state.ui)
  const organizationId = currentUser.organizationid
  const isBaltoAdmin = organizationId === 1
  const organizationOptions = getOrganizationOptions(organizations)
  const { active_channels: activeChannels = [], qa_copilot_enabled: qaCopilotEnabled = false } =
    useSelector((state) => state.currentOrganization)
  const hasQaCopilotAccess = qaCopilotEnabled && currentUser.edit_qa_copilot
  const userCanCreateCopilotScorecards =
    currentUser.prompt_editor || hasQaCopilotAccess || isBaltoAdmin

  const isQACopilot = location.pathname.startsWith('/qa-copilot')
  const isCoaching = location.pathname.startsWith('/coaching')

  const isOmniChannelEnabled = useMemo(() => {
    if (isBaltoAdmin) {
      const selectedOrganization = organizations?.find((org) => org.id === filters.organization)
      return hasAnyOmnichannelAccess(selectedOrganization?.active_channels)
    }
    return hasAnyOmnichannelAccess(activeChannels)
  }, [filters?.organization])

  let scorecardType = 'automated'
  let pageTitle = 'Scorecards'

  if (isQACopilot) {
    scorecardType = 'copilot'
    pageTitle = 'Quality Scorecards'
  } else if (isCoaching) {
    scorecardType = 'coaching'
    pageTitle = 'Coaching Scorecards'
  }

  const handleFilterChange = (section, value) => {
    dispatch(setFilter(section, value))
  }

  const openScorecardModal = () => {
    dispatch(openModal('newScorecard'))
  }

  // Manual setup routes to the prompt-editor-gated builder; QA-copilot-only users get guided only.
  const canUseManualSetup = currentUser.prompt_editor || isBaltoAdmin
  const createCopilotScorecardOptions = [
    {
      value: 'manual',
      label: <span data-testid="create-qa-copilot-scorecard-manual">Manual Setup</span>,
    },
    {
      value: 'guided',
      label: (
        <span
          className="flex-align-center small-gap"
          data-testid="create-qa-copilot-scorecard-guided"
        >
          Guided Setup
          <IconSparkles className="icon-svg status-magical" />
        </span>
      ),
    },
  ].filter((option) => option.value !== 'manual' || canUseManualSetup)

  const handleCreateCopilotScorecard = (value) => {
    if (value === 'guided') {
      dispatch(openModal('qaCopilotGuidedSetup'))
    } else {
      history.push('/qa-copilot/create')
    }
  }

  const clearOrganization = () => {
    dispatch(clearFilters(true))
    handleFilterChange('organization', '')
    dispatch(setData('scorecardConfigs', []))
    dispatch(setData('currentOrganizationId', ''))
  }

  const handleOrgSelect = (option, action) => {
    if (action.action === 'clear') {
      clearOrganization()
    } else {
      handleFilterChange('organization', option.value)
      dispatch(fetchScorecardsByOrg(option.value, scorecardType))
    }
  }

  useEffect(() => {
    // TODO: Update to not use loadConfigs
    const requestedProperties = 'name,cid,organization_id,organization_name'
    dispatch(loadPlaybooks({ organizationId, requestedProperties }))

    // Make Balto user select an org to view scorecards
    if (isBaltoAdmin) {
      clearOrganization()
      // Default it to non existent org if not found.
    } else {
      const credentials = currentUser
      const organizationid = credentials ? credentials.organizationid : 0
      dispatch(fetchScorecardsByOrg(organizationId, scorecardType))
      dispatch(setCurrentOrganizationId(organizationid))
    }
  }, [])

  return (
    <div data-testid="scorecards-page">
      <PageHeader
        title={pageTitle}
        actions={
          <>
            {scorecardType === 'automated' && (
              <Button brand data-testid="add_scorecard" onClick={openScorecardModal}>
                Create Scorecard
              </Button>
            )}
            {isQACopilot &&
              userCanCreateCopilotScorecards &&
              (createCopilotScorecardOptions.length === 1 ? (
                <Button
                  brand
                  data-testid="create-qa-copilot-scorecard"
                  onClick={() =>
                    handleCreateCopilotScorecard(createCopilotScorecardOptions[0].value)
                  }
                >
                  Create Scorecard
                </Button>
              ) : (
                <SearchableDropdown
                  hideSearch
                  fitContent
                  buttonLabel="Create Scorecard"
                  buttonProps={{
                    brand: true,
                    className: 'create-qa-copilot-scorecard-trigger',
                    'data-testid': 'create-qa-copilot-scorecard',
                  }}
                  options={createCopilotScorecardOptions}
                  handleSelect={handleCreateCopilotScorecard}
                  placement="bottom-end"
                />
              ))}
            {isCoaching && userCanCreateCopilotScorecards && (
              <Link
                to={`/${scorecardType === 'copilot' ? 'qa-copilot' : 'coaching/scorecards'}/create`}
                className="balto-button brand"
                data-testid="create-qa-copilot-scorecard"
              >
                Create Scorecard
              </Link>
            )}
          </>
        }
      />

      {isBaltoAdmin && (
        <FilterForm small>
          <OrganizationFilter
            options={organizationOptions}
            value={filters.organization}
            onChange={handleOrgSelect}
          />
        </FilterForm>
      )}

      {modal === 'newScorecard' && <ScorecardWizard />}

      {modal === 'qaCopilotGuidedSetup' && <QACopilotGuidedSetupModal />}

      <ScorecardsTable scorecardType={scorecardType} isOmniChannelEnabled={isOmniChannelEnabled} />
    </div>
  )
}

export default Scorecards
